import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { StyleSheet } from 'aphrodite';
import { useNavigate, Link, useInRouterContext } from 'react-router-dom-v5-compat';
import { keys, addStyle } from '@khanacademy/wonder-blocks-core';
import { border, semanticColor } from '@khanacademy/wonder-blocks-tokens';

const getAppropriateTriggersForRole = role => {
  switch (role) {
    case "link":
      return {
        triggerOnEnter: true,
        triggerOnSpace: false
      };
    case "checkbox":
    case "radio":
    case "listbox":
      return {
        triggerOnEnter: false,
        triggerOnSpace: true
      };
    case "button":
    case "menuitem":
    case "menu":
    case "option":
    default:
      return {
        triggerOnEnter: true,
        triggerOnSpace: true
      };
  }
};
const disabledHandlers = {
  onClick: () => void 0,
  onMouseEnter: () => void 0,
  onMouseLeave: () => void 0,
  onMouseDown: () => void 0,
  onMouseUp: () => void 0,
  onTouchStart: () => void 0,
  onTouchEnd: () => void 0,
  onTouchCancel: () => void 0,
  onKeyDown: () => void 0,
  onKeyUp: () => void 0
};
const startState = {
  hovered: false,
  focused: false,
  pressed: false,
  waiting: false
};
class ClickableBehavior extends React.Component {
  static getDerivedStateFromProps(props, state) {
    if (props.disabled) {
      return _extends({}, startState, {
        focused: state.focused
      });
    } else {
      return null;
    }
  }
  constructor(props) {
    super(props);
    this.waitingForClick = void 0;
    this.enterClick = void 0;
    this.handleClick = e => {
      const {
        onClick = undefined,
        beforeNav = undefined,
        safeWithNav = undefined
      } = this.props;
      if (this.enterClick) {
        return;
      }
      if (onClick || beforeNav || safeWithNav) {
        this.waitingForClick = false;
      }
      this.runCallbackAndMaybeNavigate(e);
    };
    this.handleMouseEnter = e => {
      if (!this.waitingForClick) {
        this.setState({
          hovered: true
        });
      }
    };
    this.handleMouseLeave = () => {
      if (!this.waitingForClick) {
        this.setState({
          hovered: false,
          pressed: false,
          focused: false
        });
      }
    };
    this.handleMouseDown = e => {
      if (this.props.onMouseDown) {
        this.props.onMouseDown(e);
      }
      this.setState({
        pressed: true
      });
    };
    this.handleMouseUp = e => {
      if (this.props.onMouseUp) {
        this.props.onMouseUp(e);
      }
      this.setState({
        pressed: false,
        focused: false
      });
    };
    this.handleTouchStart = () => {
      this.setState({
        pressed: true
      });
    };
    this.handleTouchEnd = () => {
      this.setState({
        pressed: false
      });
      this.waitingForClick = true;
    };
    this.handleTouchCancel = () => {
      this.setState({
        pressed: false
      });
      this.waitingForClick = true;
    };
    this.handleKeyDown = e => {
      const {
        onKeyDown,
        role
      } = this.props;
      if (onKeyDown) {
        onKeyDown(e);
      }
      const keyName = e.key;
      const {
        triggerOnEnter,
        triggerOnSpace
      } = getAppropriateTriggersForRole(role);
      if (triggerOnEnter && keyName === keys.enter || triggerOnSpace && keyName === keys.space) {
        e.preventDefault();
        this.setState({
          pressed: true
        });
      } else if (!triggerOnEnter && keyName === keys.enter) {
        this.enterClick = true;
      }
    };
    this.handleKeyUp = e => {
      const {
        onKeyUp,
        role
      } = this.props;
      if (onKeyUp) {
        onKeyUp(e);
      }
      const keyName = e.key;
      const {
        triggerOnEnter,
        triggerOnSpace
      } = getAppropriateTriggersForRole(role);
      if (triggerOnEnter && keyName === keys.enter || triggerOnSpace && keyName === keys.space) {
        this.setState({
          pressed: false,
          focused: true
        });
        this.runCallbackAndMaybeNavigate(e);
      } else if (!triggerOnEnter && keyName === keys.enter) {
        this.enterClick = false;
      }
    };
    this.handleFocus = e => {
      const {
        onFocus
      } = this.props;
      this.setState({
        focused: true
      }, () => {
        if (onFocus) {
          onFocus(e);
        }
      });
    };
    this.handleBlur = e => {
      this.setState({
        focused: false,
        pressed: false
      });
    };
    this.state = startState;
    this.waitingForClick = false;
    this.enterClick = false;
  }
  navigateOrReset(shouldNavigate) {
    if (shouldNavigate) {
      const {
        navigate,
        href,
        skipClientNav,
        target = undefined
      } = this.props;
      if (href) {
        if (target === "_blank") {
          window.open(href, "_blank");
          this.setState({
            waiting: false
          });
        } else if (navigate && !skipClientNav) {
          navigate(href);
          this.setState({
            waiting: false
          });
        } else {
          window.location.assign(href);
        }
      }
    } else {
      this.setState({
        waiting: false
      });
    }
  }
  handleSafeWithNav(safeWithNav, shouldNavigate) {
    const {
      skipClientNav,
      navigate
    } = this.props;
    if (navigate && !skipClientNav || this.props.target === "_blank") {
      safeWithNav();
      this.navigateOrReset(shouldNavigate);
      return Promise.resolve();
    } else {
      if (!this.state.waiting) {
        this.setState({
          waiting: true
        });
      }
      return safeWithNav().then(() => {
        if (!this.state.waiting) {
          this.setState({
            waiting: true
          });
        }
        return;
      }).catch(error => {}).finally(() => {
        this.navigateOrReset(shouldNavigate);
      });
    }
  }
  runCallbackAndMaybeNavigate(e) {
    const {
      onClick = undefined,
      beforeNav = undefined,
      safeWithNav = undefined,
      href,
      type
    } = this.props;
    let shouldNavigate = true;
    let canSubmit = true;
    if (onClick) {
      onClick(e);
    }
    if (e.defaultPrevented) {
      shouldNavigate = false;
      canSubmit = false;
    }
    e.preventDefault();
    if (!href && type === "submit" && canSubmit) {
      let target = e.currentTarget;
      while (target) {
        if (target instanceof window.HTMLFormElement) {
          const event = new window.Event("submit", {
            bubbles: true,
            cancelable: true
          });
          target.dispatchEvent(event);
          break;
        }
        target = target.parentElement;
      }
    }
    if (beforeNav) {
      this.setState({
        waiting: true
      });
      beforeNav().then(() => {
        if (safeWithNav) {
          return this.handleSafeWithNav(safeWithNav, shouldNavigate);
        } else {
          return this.navigateOrReset(shouldNavigate);
        }
      }).catch(() => {});
    } else if (safeWithNav) {
      return this.handleSafeWithNav(safeWithNav, shouldNavigate);
    } else {
      this.navigateOrReset(shouldNavigate);
    }
  }
  render() {
    const rel = this.props.rel || (this.props.target === "_blank" ? "noopener noreferrer" : undefined);
    const childrenProps = this.props.disabled ? _extends({}, disabledHandlers, {
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      tabIndex: this.props.tabIndex,
      rel
    }) : {
      onClick: this.handleClick,
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
      onMouseDown: this.handleMouseDown,
      onMouseUp: this.handleMouseUp,
      onTouchStart: this.handleTouchStart,
      onTouchEnd: this.handleTouchEnd,
      onTouchCancel: this.handleTouchCancel,
      onKeyDown: this.handleKeyDown,
      onKeyUp: this.handleKeyUp,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      tabIndex: this.props.tabIndex,
      rel
    };
    const {
      children
    } = this.props;
    return children && children(this.state, childrenProps);
  }
}
ClickableBehavior.defaultProps = {
  disabled: false
};

const isClientSideUrl = href => {
  if (typeof href !== "string") {
    return false;
  }
  return !/^(https?:)?\/\//i.test(href) && !/^([^#]*#[\w-]*|[\w\-.]+:)/.test(href);
};

function withRouter(Component) {
  function WithRouterWrapper(props) {
    const navigate = useNavigate();
    return React.createElement(Component, _extends({}, props, {
      navigate: navigate
    }));
  }
  WithRouterWrapper.displayName = "withRouter(ClickableBehavior)";
  return WithRouterWrapper;
}
const ClickableBehaviorWithRouter = withRouter(ClickableBehavior);
function getClickableBehavior(href, skipClientNav, inRouterContext) {
  if (inRouterContext && skipClientNav !== true && href && isClientSideUrl(href)) {
    return ClickableBehaviorWithRouter;
  }
  return ClickableBehavior;
}

const _excluded = ["href", "onClick", "skipClientNav", "beforeNav", "safeWithNav", "style", "target", "testId", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "onMouseUp", "hideDefaultFocusRing", "light", "disabled", "tabIndex"];
const StyledA = addStyle("a");
const StyledButton = addStyle("button");
const StyledLink = addStyle(Link);
const Clickable = React.forwardRef(function Clickable(props, ref) {
  const getCorrectTag = (clickableState, inRouterContext, commonProps) => {
    const activeHref = props.href && !props.disabled;
    const useClient = inRouterContext && !props.skipClientNav && isClientSideUrl(props.href || "");
    if (activeHref && useClient && props.href) {
      return React.createElement(StyledLink, _extends({}, commonProps, {
        to: props.href,
        role: props.role,
        target: props.target || undefined,
        "aria-disabled": props.disabled ? "true" : "false",
        ref: ref
      }), props.children(clickableState));
    } else if (activeHref && !useClient) {
      return React.createElement(StyledA, _extends({}, commonProps, {
        href: props.href,
        role: props.role,
        target: props.target || undefined,
        "aria-disabled": props.disabled ? "true" : "false",
        ref: ref
      }), props.children(clickableState));
    } else {
      return React.createElement(StyledButton, _extends({}, commonProps, {
        type: "button",
        "aria-disabled": props.disabled,
        ref: ref
      }), props.children(clickableState));
    }
  };
  const inRouterContext = useInRouterContext();
  const {
      href,
      onClick,
      skipClientNav,
      beforeNav = undefined,
      safeWithNav = undefined,
      style,
      target = undefined,
      testId,
      onFocus,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseUp,
      hideDefaultFocusRing,
      light,
      disabled,
      tabIndex
    } = props,
    restProps = _objectWithoutPropertiesLoose(props, _excluded);
  const ClickableBehavior = getClickableBehavior(href, skipClientNav, inRouterContext);
  const getStyle = state => [styles.reset, styles.link, !hideDefaultFocusRing && state.focused && (light ? styles.focusedLight : styles.focused), disabled && styles.disabled, style];
  if (beforeNav) {
    return React.createElement(ClickableBehavior, {
      href: href,
      onClick: onClick,
      beforeNav: beforeNav,
      safeWithNav: safeWithNav,
      onFocus: onFocus,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      onMouseDown: onMouseDown,
      onMouseUp: onMouseUp,
      disabled: disabled,
      tabIndex: tabIndex
    }, (state, childrenProps) => getCorrectTag(state, inRouterContext, _extends({}, restProps, {
      "data-testid": testId,
      style: getStyle(state)
    }, childrenProps)));
  } else {
    return React.createElement(ClickableBehavior, {
      href: href,
      onClick: onClick,
      safeWithNav: safeWithNav,
      onFocus: onFocus,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      onMouseDown: onMouseDown,
      onMouseUp: onMouseUp,
      target: target,
      disabled: disabled,
      tabIndex: tabIndex
    }, (state, childrenProps) => getCorrectTag(state, inRouterContext, _extends({}, restProps, {
      "data-testid": testId,
      style: getStyle(state)
    }, childrenProps)));
  }
});
Clickable.defaultProps = {
  light: false,
  disabled: false
};
const styles = StyleSheet.create({
  reset: {
    border: "none",
    margin: 0,
    padding: 0,
    width: "auto",
    overflow: "visible",
    background: "transparent",
    textDecoration: "none",
    color: "inherit",
    font: "inherit",
    boxSizing: "border-box",
    touchAction: "manipulation",
    userSelect: "none",
    outline: "none",
    lineHeight: "normal",
    WebkitFontSmoothing: "inherit",
    MozOsxFontSmoothing: "inherit"
  },
  link: {
    cursor: "pointer"
  },
  focused: {
    ":focus": {
      outline: `solid ${border.width.medium} ${semanticColor.focus.outer}`
    }
  },
  focusedLight: {
    outline: `solid ${border.width.medium} ${semanticColor.border.inverse}`
  },
  disabled: {
    color: semanticColor.action.secondary.disabled.foreground,
    cursor: "not-allowed",
    ":focus": {
      outline: "none"
    },
    ":focus-visible": {
      outline: `solid ${border.width.medium} ${semanticColor.focus.outer}`
    }
  }
});

export { ClickableBehavior, Clickable as default, getClickableBehavior, isClientSideUrl };
